import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Switch,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import * as React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { PaywallModal } from '@/components/Paywall';
import { InfoIcon } from '@/imports/ui/chakra/feather';

import { AudienceMenu } from './components/AudienceMenu';
import { useSponsorTierData } from './hooks/useSponsorTierData';

export const SponsoredPostSelector: React.FC<{
  organizationId: string;
}> = ({ organizationId }) => {
  const { data, loading } = useSponsorTierData(organizationId);
  const currentTier = data?.sponsorship?.currentTier;
  const userOrganizationPermission = data?.currentUserOrganizationPermission;

  const paywall = useDisclosure();

  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<{
    isSponsoredPost: boolean;
    group: string;
  }>();
  const isSponsoredPost = watch('isSponsoredPost');

  const formattedIntervalEndDate = currentTier?.sponsoredPostsIntervalEndDate
    ? dayjs(currentTier.sponsoredPostsIntervalEndDate).format('MMMM D')
    : null;

  if (loading) {
    return null;
  }

  return (
    <Stack>
      <HStack justify="space-between">
        <Box>
          <HStack spacing="0">
            <Heading size="sm">Mark this Post as Sponsored</Heading>
            <Popover trigger="hover">
              <PopoverTrigger>
                <IconButton
                  aria-label="sponsored post tooltip"
                  variant="unstyled"
                  icon={<InfoIcon color="gray" mb="1" />}
                  size="sm"
                />
              </PopoverTrigger>
              <PopoverContent bg="gray.700" color="white">
                <PopoverArrow bg="gray.700" />
                <PopoverBody>
                  <Text textStyle="compact" fontWeight="semibold">
                    Marking a post as sponsored means your post will reach a
                    wider audience than those who already follow your page.
                    {!userOrganizationPermission?.canCreateOrganizationSponsoredPosts && (
                      <>
                        Sponsored posts are a paid feature.{' '}
                        <Button
                          variant="link"
                          onClick={paywall.onOpen}
                          colorScheme="black"
                        >
                          Upgrade to a Paid Tier
                        </Button>
                      </>
                    )}
                  </Text>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </HStack>
          <Text color="gray.500">
            {!!userOrganizationPermission?.canCreateOrganizationSponsoredPosts ? (
              <>
                You have{' '}
                {currentTier?.numSponsoredPostsRemaining === 0
                  ? 'no more'
                  : currentTier?.numSponsoredPostsRemaining}{' '}
                sponsored post
                {currentTier?.numSponsoredPostsRemaining === 1 ? '' : 's'} left
                to use by {formattedIntervalEndDate}
              </>
            ) : (
              <>
                <Button
                  variant="link"
                  colorScheme="black"
                  onClick={paywall.onOpen}
                >
                  Upgrade to a Paid Tier
                </Button>{' '}
                to get access to this functionality
              </>
            )}
          </Text>
        </Box>
        <Box>
          <FormControl>
            <Switch
              mr="2"
              size="md"
              isDisabled={
                !userOrganizationPermission?.canCreateOrganizationSponsoredPosts ||
                currentTier?.numSponsoredPostsRemaining === 0
              }
              {...register('isSponsoredPost')}
            />
          </FormControl>
        </Box>
      </HStack>

      {isSponsoredPost && (
        <FormControl>
          <FormLabel>Select Audience</FormLabel>
          <Controller
            name="group"
            rules={{ required: 'Audience is required' }}
            render={({ field: { onChange, value, ref, onBlur } }) => (
              <AudienceMenu
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                ref={ref}
              />
            )}
          />
          <FormErrorMessage>{errors.group?.message}</FormErrorMessage>
        </FormControl>
      )}

      <PaywallModal
        isOpen={paywall.isOpen}
        onClose={paywall.onClose}
        organizationId={organizationId}
        headerText={currentTier?.postUpgradeText?.title ?? ''}
        bodyText={currentTier?.postUpgradeText?.subtitle ?? ''}
        shouldShowUpgradeCTA={currentTier?.shouldShowUpgradeCTA}
        source="sponsored post upgrade modal"
      />
    </Stack>
  );
};
